// PRD 05 §3.9 — read-only score box rendered inside the collapsible "추가 설정"
// block on a question card. Shows the 4 configured values. Toggle/edit controls
// live in the edit panel.
//
// SHCT-603 — the derived 정규화값 / 최종값 cells and the formula line are removed
// here too, not only in the edit panel: ADM.040 (DONE) and PRD 05 §3.9 scope the
// removal to the whole design screen — "정규화값·최종값 derived 표시와 산식 미리보기는
// 설계 화면에서 제거". QC filed the panel instance; leaving the card instance would
// have kept the same 4-decimal derived values one click away.

import { useTranslation } from 'react-i18next';

import type { Question } from '../types/questionnaire-design.types';

function ScoreCell({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ color: 'var(--muted-foreground)', marginBottom: 1 }}>{label}</div>
      <div style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--foreground)' }}>
        {children}
      </div>
    </div>
  );
}

interface ScoringBoxReadOnlyProps {
  question: Question;
}

export function ScoringBoxReadOnly({ question }: ScoringBoxReadOnlyProps) {
  const { t } = useTranslation('questionnaire-design');
  const adv = question.advanced;

  // PRD 05 §3.9.2 (v1.14) — on the card (display) area, a question excluded from
  // the R-value (scoreContribution=false) HIDES the score-settings block (4 inputs
  // + formula) entirely and shows a single "산출 제외" notice line instead. (The
  // edit panel keeps the greyed/disabled inputs — that lives in ScoringSettingsSection.)
  if (!adv.scoreContribution) {
    return (
      <div
        style={{
          border: '1.5px dashed var(--border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-3) var(--space-4)',
          background: '#FAFBFC',
          marginTop: 12,
          fontSize: 'var(--font-size-xs)',
          color: 'var(--muted-foreground)',
        }}
      >
        🚫 {t('scoringBox.excludedNotice')}
      </div>
    );
  }

  return (
    <div
      style={{
        border: '1.5px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4) var(--space-4) var(--space-3)',
        background: '#FAFBFC',
        position: 'relative',
        marginTop: 12,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: -9,
          left: 12,
          background: '#FAFBFC',
          padding: '0 6px',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--muted-foreground)',
        }}
      >
        {t('scoringBox.title')}
      </span>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 'var(--space-2) var(--space-4)',
          fontSize: 'var(--font-size-xs)',
        }}
      >
        <ScoreCell label={t('scoringBox.fields.weight')}>
          {adv.weight.toFixed(1)}
        </ScoreCell>
        <ScoreCell label={t('scoringBox.fields.reliability')}>
          {adv.reliability.toFixed(1)}
        </ScoreCell>
        <ScoreCell label={t('scoringBox.fields.min')}>{adv.min}</ScoreCell>
        <ScoreCell label={t('scoringBox.fields.max')}>{adv.max}</ScoreCell>
      </div>
    </div>
  );
}
